import { useTranslation } from 'react-i18n-lite'

import {
  AvatarList,
  Divider,
  DocumentListCard,
  formatDateTime,
  SafeHtmlContent,
  SwipeableSlider,
  Text,
  YesObject
} from '@yes.technology/react-toolkit'

import { updateStatus } from 'status/shared'
import { StatusAction } from 'status/shared/situation.type'

import { useFetchYesObjects } from 'yesObject/shared'

import TaskActions from './TaskActions'

import { TaskPriority } from './TaskObjectCard.types'
import { Container, Content, PriorityBadge } from './TaskObjectCard.styles'

import { useEffect, useMemo, useRef, useState } from 'react'
import InformationalFadingContainer from 'site/Renderer/components/InformationalFadingContainer/InformationalFadingContainer'
import useKanbanContext from 'site/Renderer/components/Kanban/components/KanbanAutoColumns/useKanbanContext'
import TaskFieldsRow from './TaskFields'
import { TaskObjectCardProps } from './TaskObjectCard.types'
import useAssociatedUsers from './hooks/useAssociatedUsers/useAssociatedUsers'

type AllowedLocales = 'pt-BR' | 'en-US' | 'de-DE' | 'es-419'

function taskToAvatarListAdapter(objects: YesObject[]) {
  return objects.map((object) => ({
    ...object,
    title: object.des
  }))
}

const defaultPriority: TaskPriority = 'normal'

export default function TaskObjectCard({
  priority = defaultPriority,
  des,
  sid,
  complement,
  date_start_row: startDate,
  date_creation_row: submissionDate,
  uuid_status: statusUuid,
  version,
  uuid,
  uuid_Objectclass,
  returnOnFinish,
  buttonProps,
  ...otherProps
}: TaskObjectCardProps) {
  const { t, language } = useTranslation()

  const containerRef = useRef<HTMLDivElement>(null)

  const [renderedItemSettings, setRenderedItemSettings] = useState<{
    bottomListLimit: number
    amountOfVisibleItems: number
  }>({
    bottomListLimit: 4,
    amountOfVisibleItems: 2
  })

  const { bottomListLimit, amountOfVisibleItems } = renderedItemSettings

  const { yesObjects: statusActions } = useFetchYesObjects<StatusAction>({
    filterBody: {
      query: { $and: [{ 'allowed_origin_status.code': { $eq: statusUuid } }] },
      objectclass: window.uuidObjectClassStatusAction
    }
  })
  const {
    onStatusChange,
    statusActionClassification,
    associatedUsersRelations,
    visualizeUrl
  } = useKanbanContext()

  const { data: associatedUsers } = useAssociatedUsers({
    taskUuid: uuid,
    associatedUsersRelations,
    data: { ...otherProps }
  })

  const classification = useMemo(
    () =>
      statusActionClassification?.find(
        (classification) => classification.UUID_OBJECT === statusUuid
      ),
    [statusActionClassification, statusUuid]
  )

  const changeStatus = (destinationStatusCode: string) => {
    updateStatus({
      objectVersion: version,
      objectUuid: uuid,
      objectclassUuid: uuid_Objectclass,
      destinationStatusCode,
      showLoading: false
    })

    onStatusChange?.(uuid, destinationStatusCode)
  }

  useEffect(() => {
    if (!containerRef.current) return

    function handleResize([contentRect]: ResizeObserverEntry[]) {
      const width = contentRect.contentRect.width

      if (width <= 270 && width > 240) {
        setRenderedItemSettings((prev) => ({
          ...prev,
          bottomListLimit: 2,
          amountOfVisibleItems: 2
        }))
      } else if (width <= 240) {
        setRenderedItemSettings((prev) => ({
          ...prev,
          bottomListLimit: 2,
          amountOfVisibleItems: 1
        }))
      } else {
        setRenderedItemSettings((prev) => ({
          ...prev,
          bottomListLimit: 4,
          amountOfVisibleItems: 2
        }))
      }
    }

    const observer = new ResizeObserver(handleResize)

    observer.observe(containerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  const associatedUsersList = Object.entries(associatedUsers) || []

  const priorityValue =
    typeof priority === 'string'
      ? priority
      : ((priority?.[0]?.complement || defaultPriority) as TaskPriority)

  return (
    <Container ref={containerRef}>
      <DocumentListCard
        style={{ padding: '16px 8px 8px', border: '1.5px solid #BCBCBE' }}
      >
        <Content>
          <PriorityBadge priority={priorityValue}>
            {t(`task.priority.${priorityValue}`)}
          </PriorityBadge>
          <TaskFieldsRow
            firstField={{
              label: t('task.model'),
              value: des
            }}
            secondField={{
              label: t('task.sid'),
              value: sid ?? ''
            }}
            gridTemplate='minmax(0, 7fr) minmax(0, 3fr)'
          />
          <h2>{des}</h2>
          {complement && (
            <InformationalFadingContainer>
              <Text variant='content-03'>
                <SafeHtmlContent html={complement} />
              </Text>
            </InformationalFadingContainer>
          )}
          <Divider heightClass='small' color='lightGray' />
          <SwipeableSlider itemsPerPage={amountOfVisibleItems}>
            {associatedUsersList.map(([role, objects], index) => {
              return (
                <div
                  style={{
                    marginRight:
                      index < associatedUsersList.length ? '0.5rem' : 'none'
                  }}
                  key={role}
                >
                  <AvatarList
                    objects={taskToAvatarListAdapter(objects)}
                    title={role}
                    backgroundColor='#BCBCBE'
                    bottomListLimit={bottomListLimit}
                  />
                </div>
              )
            })}
          </SwipeableSlider>
          <Divider heightClass='small' color='lightGray' />
          <TaskFieldsRow
            firstField={{
              label: t('task.scheduled-submission'),
              value: formatDateTime(new Date(submissionDate), {
                locale: language as AllowedLocales,
                format: 'date-time',
                timezone: window.waTimezone
              })
            }}
            secondField={{
              label: t('task.scheduled-start'),
              value: formatDateTime(new Date(startDate), {
                locale: language as AllowedLocales,
                format: 'date-time',
                timezone: window.waTimezone
              })
            }}
            gridTemplate='minmax(0, 5fr) minmax(0, 5fr)'
          />
          <TaskActions
            {...{
              objectclassUuid: uuid_Objectclass,
              statusActions,
              changeStatus,
              statusUuid,
              returnOnFinish,
              taskUuid: uuid,
              des,
              buttonProps,
              visualizeUrl,
              classification
            }}
          />
        </Content>
      </DocumentListCard>
    </Container>
  )
}
